import { Close, Description } from '@mui/icons-material'
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, timelineItemClasses, TimelineSeparator } from '@mui/lab'
import { Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, IconButton, Modal, Typography } from '@mui/material'
import { useState } from 'react'
import ApiClient from '../../../../backend/ApiClient'
import { IContract, ISimpleContractWithApproval } from '../../../../definitions/Contract'
import Formatter from '../../../../helpers/Formatter'
import useQuery, { buildQueryKey } from '../../../hooks/useQuery'
import { useDispatch } from 'react-redux'
import { changeRoute } from '../../../../store/slices/SessionSlice'
import { statusOptions } from '../../../../definitions/Approval'
import PermissionsWrapper from '../../../permissions/PermissionsWrapper'

interface P {
    open: boolean
    onClose: () => void
    contract: IContract
    addVersionLoading?: boolean
    onAddVersion: (newVersion: ISimpleContractWithApproval) => void
}

function ContractVersioningModal({ open, onClose, contract, addVersionLoading, onAddVersion }: P) {
    const dispatch = useDispatch()
    const [versionCloning, setVersionCloning] = useState<string>(null)

    const { status, response: versions } = useQuery<ISimpleContractWithApproval[]>({
        queryKey: buildQueryKey('contract-versions', contract?.versioning?.ancestorKey),
        queryFn: async () => contract?.versioning?.ancestorKey ? await ApiClient.getContractVersions(contract?.versioning?.ancestorKey) : null,
        cache: true
    })

    const handleAddVersion = async (newVersion: ISimpleContractWithApproval) => {
        setVersionCloning(newVersion.uuid)
        await onAddVersion(newVersion)
        onClose()
    }

    const formatDate = (dateString: string) => {
        return Formatter.date(new Date(dateString))
    }

    const onSelectVersion = (versionContract: ISimpleContractWithApproval) => {
        dispatch(changeRoute({
            route: 'contract-editor',
            params: {
                contractUUID: versionContract.uuid,
                uploadedFiles: versionContract.files || []
            }
        }))
        onClose()
    }

    const orderedVersions = versions?.sort((a, b) => (b.versioning?.version || 0) - (a.versioning?.version || 0))
    const isLoading = status === 'loading' || status === 'stale'

    return (
        <Modal open={open} onClose={onClose}>
            <Box margin='130px auto auto' maxWidth='60vh' overflow='auto'>
                <Card>
                    <Box display='flex' justifyContent='space-between' alignContent='end'>
                        <CardHeader title='Contract Settings' />
                        <IconButton
                            onClick={onClose}
                            size='small'
                            sx={{ m: 2 }}>
                            <Close />
                        </IconButton>
                    </Box>
                    <CardContent style={{ height: '60vh', flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                        <Box>
                            <Typography variant='important' marginBottom={2}>Contract Information</Typography>
                            <Box sx={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', paddingTop: 2, paddingBottom: 2, paddingRight: 2, gap: 1 }}>
                                <Typography>Name: {contract?.name || 'Settlement Engine'}</Typography>
                                <Typography>Created at: {contract?.createdAt ? formatDate(contract?.createdAt) : formatDate(contract?.lastUpdate || new Date().toISOString())}</Typography>
                                <Typography>Last Modify: {formatDate(contract?.lastUpdate || new Date().toISOString())}</Typography>
                                <Typography>Version: {contract?.versioning?.version || 1}</Typography>
                            </Box>
                        </Box>
                        <Box marginTop={2} sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                            <Typography variant='important'>
                                Versioning
                            </Typography>
                            <Box sx={{ overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', paddingTop: 2, paddingBottom: 2, paddingRight: 2 }}>
                                <Timeline sx={{ [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 } }}>
                                    {isLoading
                                        ? (
                                                <Box display='flex' justifyContent='center' p={4}>
                                                    <CircularProgress />
                                                </Box>
                                            )
                                        : orderedVersions?.map(version => {
                                            const isThisContract = version?.uuid === contract?.uuid
                                            return (
                                                <TimelineItem key={version.uuid}>
                                                    <TimelineSeparator>
                                                        <TimelineConnector />
                                                        <TimelineDot color={isThisContract ? 'primary' : 'inherit'}>

                                                            <Description />
                                                        </TimelineDot>
                                                        <TimelineConnector />
                                                    </TimelineSeparator>
                                                    <TimelineContent>
                                                        <Box display='flex' alignItems='center' gap={1} marginBottom={0.5}>
                                                            <Typography variant='h6' component='span' fontWeight={version?.versioning?.isHead ? 'bold' : 'regular'}>
                                                                Version {version.versioning?.version || 1}
                                                            </Typography>
                                                            <Chip
                                                                sx={{
                                                                    borderRadius: '999px',
                                                                    fontWeight: 600,
                                                                    ml: 2
                                                                }}
                                                                label={(
                                                                    <Box display='flex' alignItems='center' gap={1}>
                                                                        <Box
                                                                            width={8}
                                                                            height={8}
                                                                            borderRadius='50%'
                                                                            bgcolor={
                                                                                (version?.approval?.status === 'approved' && version.versioning.isHead)
                                                                                    ? 'success.main'
                                                                                    : (version?.approval?.status === 'approved' && !version.versioning.isHead)
                                                                                            ? 'grey.500'
                                                                                            : version?.approval?.status === 'ready-for-review'
                                                                                                ? 'info.main'
                                                                                                : version?.approval?.status === 'rejected'
                                                                                                    ? 'red'
                                                                                                    : 'orange'
                                                                            }
                                                                        />
                                                                        <Typography variant='body2'>
                                                                            {statusOptions?.find(option => option.value === version?.approval?.status)?.label || 'No Approval'}
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                                variant='outlined'
                                                                size='medium'
                                                            />
                                                        </Box>
                                                        <Typography variant='body2' marginLeft={0} color='text.secondary'>
                                                            {formatDate(version.lastUpdate || new Date().toISOString())}
                                                        </Typography>
                                                        <Box marginTop={1} gap={2} display='flex' flexDirection='row'>
                                                            {!isThisContract && (
                                                                <Button variant='contained' disabled={isLoading} onClick={() => onSelectVersion(version)}>
                                                                    View
                                                                </Button>
                                                            )}
                                                            <PermissionsWrapper permission='contracts.edit'>
                                                                <Button disabled={addVersionLoading} onClick={() => handleAddVersion(version)}>
                                                                    <Box sx={{ alignItems: 'center', justifyContent: 'center', display: 'flex', flexDirection: 'row' }}>
                                                                        {(addVersionLoading && versionCloning === version.uuid) && <CircularProgress size={15} sx={{ marginRight: 1 }} />}
                                                                        <Typography>New Version</Typography>
                                                                    </Box>
                                                                </Button>
                                                            </PermissionsWrapper>
                                                        </Box>
                                                    </TimelineContent>
                                                </TimelineItem>
                                            )
                                        })}
                                </Timeline>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Modal>
    )
}

export default ContractVersioningModal
